import { Spin } from 'antd'
import { ComponentProps } from 'react'
import { Bar } from 'react-chartjs-2'
import { useHomeCareOrderedHoursIntervalComparisonValues, useMunicipalityName } from '../../../../api/benchmarking'
import { getColorByIndex } from '../../util/colors'
import BenchmarkingContentRow from '../../components/BenchmarkingContentRow'
import { getFontSize } from '../../util/fontSize'
import { BenchmarkingLegend } from '../../components/BenchmarkingLegend'
import { BORDER_WIDTH } from '../../util/constants'
import { baseGraphOptions } from '../../util/baseGraphOptions'
import { EmptySelector } from '../../components/EmptySelector'
import { NO_COMPARISON, STATDB_NAME } from '../../util/names'

export const HomeCareOrderedHoursIntervalComparison = ({ text }: { text: React.ReactNode }) => {
  const { data: municipalityName, isLoading: isMunicipalityNameLoading } = useMunicipalityName()
  const { data, isLoading } = useHomeCareOrderedHoursIntervalComparisonValues()

  if (isLoading || isMunicipalityNameLoading) {
    return <BenchmarkingContentRow graph={<Spin size="large" />} text={text} />
  }

  if (!data) {
    return <></>
  }

  const graphOptions: ComponentProps<typeof Bar>['options'] = {
    ...baseGraphOptions,
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            return `${context.dataset.label} h: ${Math.round(context.parsed.y)}%`
          },
          title: (context) => {
            if (context[0].label.includes('brukare')) {
              return 'Andel av brukare'
            } else if (context[0].label.includes('beställd tid')) {
              return 'Andel av tid'
            }
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        min: 0,
        max: 100,
        ticks: {
          font: {
            size: getFontSize('md'),
          },
        },
      },
      y: {
        stacked: true,
        min: 0,
        max: 100,
        ticks: {
          callback: (value) => `${value}%`,
          font: {
            size: getFontSize('md'),
          },
        },
      },
    },
  }

  const graphData: ComponentProps<typeof Bar>['data'] = {
    labels: [`Andel av brukare, ${municipalityName}`, `Andel av beställd tid, ${municipalityName}`],
    datasets: data.baselineValues.map((item, index) => ({
      label: item.interval,
      data: [item.value, data.comparisonValues[index].value],
      backgroundColor: getColorByIndex(index).fill,
      borderColor: getColorByIndex(index).border,
      borderWidth: BORDER_WIDTH,
    })),
  }
  const legend = (
    <BenchmarkingLegend
      items={data.baselineValues.map((item, index) => ({
        text: item.interval,
        fillColor: getColorByIndex(index).fill,
        borderColor: getColorByIndex(index).border,
      }))}
    />
  )
  const title = 'Andel brukare jämfört med andel av beställd tid i intervall av beställd tid'
  const graph: React.ReactNode = <Bar options={graphOptions} data={graphData} />

  const dataSourceSelector = <EmptySelector value={STATDB_NAME} />
  const comparisonSelector = <EmptySelector value={NO_COMPARISON} />
  return <BenchmarkingContentRow graph={graph} text={text} title={title} legend={legend} dataSourceSelector={dataSourceSelector} comparisonSelector={comparisonSelector} />
}
